import React, { useEffect, useState } from 'react';
import { useTheme } from 'styled-components';
import { useTranslation } from 'react-i18next';
import {
  ScatterChart, Scatter, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend,
} from 'recharts';

import PanelCard from '../../performance-operations/components/PanelCard';
import { Table } from '../../performance-operations/styles';
import { usePanel } from '../../performance-operations/config';
import { Hidden, Note } from '../../../components/performance/evaluation/EvaluationParts';
import { formatNum, isSuppressed, metaOfContext } from '../../../components/performance/evaluation/evaluationAnalytics';
import { NS, fetchRaterProfile } from '../config';

// E06 orta sağ — değerlendirici grubu bazlı yetkinlik profili, NOKTA grafiği (radar yerine karşılaştırılabilir ortak 1–5 ölçek).
// Nokta = grubun kişi bazındaki yetkinlik ortalamalarının ortalaması; anonim grup yalnız bir kişide ≥ a farklı yanıtla katkı
// verdiyse girer, kişi sayısı k'nin altındaki nokta gizlenir. Yanıtlayan adı / tekil puan yok.
export const groupColor = (theme, group) => ({
  self: theme.colors.primary.main,
  manager: theme.colors.secondary.dark,
  peer: theme.colors.auxiliar.purple,
  subordinate: theme.colors.auxiliar.warning,
  senior: theme.colors.primary.dark,
}[group] || theme.colors.text.muted);

export default function RaterProfilePanel({ filters, screen, onMeta }) {
  const { t, i18n } = useTranslation();
  const theme = useTheme();
  const lang = i18n.language;
  const [table, setTable] = useState(false);
  const key = JSON.stringify([filters, screen]);
  const panel = usePanel(() => fetchRaterProfile(filters, screen), [key], !!filters.periodId);
  useEffect(() => { if (panel.data) onMeta?.('raterProfile', metaOfContext(panel.data)); }, [panel.data, onMeta]);
  const rows = panel.data?.rows || [];
  const groups = panel.data?.groups || [];
  const dot = (row, g) => row.dots.find((d) => d.group === g);
  const series = groups.map((g) => ({
    group: g,
    data: rows.map((r) => ({ name: r.name, x: dot(r, g)?.average, n: dot(r, g)?.n })).filter((p) => p.x !== null && p.x !== undefined),
  }));

  return (
    <PanelCard
      panelKey="raterProfile" title={t(`${NS}.panel.raterProfile`)} span={5}
      loading={panel.loading} error={panel.error} onRetry={panel.retry} empty={rows.length === 0}
      emptyText={t(`${NS}.raterProfile.empty`)}
      tableToggle showTable={table} onToggleTable={() => setTable((v) => !v)}
    >
      <Note>{t(`${NS}.raterProfile.hint`, { a: panel.data?.context?.anonymityThreshold ?? 3, k: panel.data?.context?.threshold ?? 5 })}</Note>
      {table ? (
        <Table>
          <thead>
            <tr>
              <th>{t(`${NS}.col.competency`)}</th>
              {groups.map((g) => <th key={g}>{t(`${NS}.raterGroup.${g}`)}</th>)}
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.competencyId} data-row={r.competencyId}>
                <td title={r.name}>{r.name}</td>
                {groups.map((g) => {
                  const d = dot(r, g);
                  if (isSuppressed(d)) return <td key={g}><Hidden ns={NS} /></td>;
                  return <td key={g} data-dot={`${r.competencyId}:${g}`}>{d?.average === null || d?.average === undefined ? '-' : `${formatNum(d.average, lang)} · ${t(`${NS}.sampleN`, { n: formatNum(d.n, lang, 0) })}`}</td>;
                })}
              </tr>
            ))}
          </tbody>
        </Table>
      ) : (
        <div style={{ width: '100%', height: Math.max(180, rows.length * 40 + 60) }}>
          <ResponsiveContainer>
            <ScatterChart margin={{ top: 8, right: 16, left: 8, bottom: 8 }}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis type="number" dataKey="x" domain={[1, 5]} ticks={[1, 2, 3, 4, 5]} tick={{ fontSize: theme.metrics.font.size.xs }} />
              <YAxis type="category" dataKey="name" allowDuplicatedCategory={false} width={120} tick={{ fontSize: theme.metrics.font.size.xs }} />
              <Tooltip formatter={(value, _name, item) => [`${formatNum(value, lang)} · ${t(`${NS}.sampleN`, { n: formatNum(item?.payload?.n, lang, 0) })}`, '']} />
              <Legend wrapperStyle={{ fontSize: theme.metrics.font.size.xs }} />
              {series.map((s) => (
                <Scatter key={s.group} name={t(`${NS}.raterGroup.${s.group}`)} data={s.data} fill={groupColor(theme, s.group)} />
              ))}
            </ScatterChart>
          </ResponsiveContainer>
        </div>
      )}
    </PanelCard>
  );
}
